import { getDocs, collection, query, where, orderBy, limit, Timestamp, getCountFromServer } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "@/lib/firebase";
import SidebarShell from "@/components/SidebarShell";
import { KpiTile } from "@/components/KpiTile";
import {
  Briefcase,
  TrendingUp,
  DollarSign,
  FileText,
  FileInput,
  UserPlus,
  Users,
  FileCheck,
  Calculator,
  Wallet,
  Clock,
} from "lucide-react";

type Kpis = {
  activeDeals: number;
  deals30: number;
  deals30Change?: string;
  pipelineValue: number;
  ndas7: number;
  ndas30: number;
  ndas30Change?: string;
  ndas90: number;
  buyerSubs30: number;
  buyerSubs30Change?: string;

  // Acquisition/Matching/Lending
  newBuyers1: number;
  newBuyers7: number;
  newBuyers30: number;
  newBuyers30Change?: string;
  engagedBuyers30: number;

  dealsWithNda: number;
  dealsWithLoi: number;

  ndaPerDealAvg: number;
  loiPerDealAvg: number;

  lenderOffers30: number;
  avgOfferAmount: number | null;
};

export default function KpiDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<Kpis | null>(null);

  const now = Timestamp.now();
  const minusDays = (d: number) =>
    Timestamp.fromMillis(now.toMillis() - d * 24 * 60 * 60 * 1000);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Collections
        const dealsCol = collection(db, "deals");
        const ndasCol = collection(db, "ndas");
        const subsCol = collection(db, "submissions");
        const usersCol = collection(db, "users");

        // Active Deals (listed/under_nda/offers/under_loi)
        const activeDealsQ = query(
          dealsCol,
          where("status", "in", ["listed", "under_nda", "offers", "under_loi"])
        );
        const activeDeals = (await getCountFromServer(activeDealsQ)).data().count;

        // New deals (30d)
        const deals30Q = query(dealsCol, where("date_listed", ">", minusDays(30)));
        const deals30 = (await getCountFromServer(deals30Q)).data().count;
        
        // New deals (60d) for comparison
        const deals60Q = query(dealsCol, where("date_listed", ">", minusDays(60)));
        const deals60 = (await getCountFromServer(deals60Q)).data().count;
        const deals30to60 = deals60 - deals30;

        // Pipeline value (placeholder - needs aggregate)
        const pipelineValue = 0;

        // NDAs
        const ndas7 = (await getCountFromServer(query(ndasCol, where("signedAt", ">", minusDays(7))))).data().count;
        const ndas30 = (await getCountFromServer(query(ndasCol, where("signedAt", ">", minusDays(30))))).data().count;
        const ndas90 = (await getCountFromServer(query(ndasCol, where("signedAt", ">", minusDays(90))))).data().count;
        
        // NDAs 60d for comparison
        const ndas60 = (await getCountFromServer(query(ndasCol, where("signedAt", ">", minusDays(60))))).data().count;
        const ndas30to60 = ndas60 - ndas30;

        // Buyer submissions (30d)
        const buyerSubs30 = (await getCountFromServer(
          query(subsCol, where("type", "==", "buyer"), where("createdAt", ">", minusDays(30)))
        )).data().count;
        
        // Buyer submissions (60d) for comparison
        const buyerSubs60 = (await getCountFromServer(
          query(subsCol, where("type", "==", "buyer"), where("createdAt", ">", minusDays(60)))
        )).data().count;
        const buyerSubs30to60 = buyerSubs60 - buyerSubs30;

        // Acquisition
        const newBuyers1 = (await getCountFromServer(query(usersCol, where("createdAt", ">", minusDays(1))))).data().count;
        const newBuyers7 = (await getCountFromServer(query(usersCol, where("createdAt", ">", minusDays(7))))).data().count;
        const newBuyers30 = (await getCountFromServer(query(usersCol, where("createdAt", ">", minusDays(30))))).data().count;
        
        // New buyers (60d) for comparison
        const newBuyers60 = (await getCountFromServer(query(usersCol, where("createdAt", ">", minusDays(60))))).data().count;
        const newBuyers30to60 = newBuyers60 - newBuyers30;

        // Engaged buyers (buyers created in last 30 days)
        const engagedBuyers30 = (await getCountFromServer(
          query(usersCol, where("roles", "array-contains", "buyer"), where("createdAt", ">", minusDays(30)))
        )).data().count;

        // Deals with NDA/LOI (placeholders)
        const dealsWithNda = ndas30;
        const dealsWithLoi = 0;

        // Matching averages (placeholders)
        const ndaPerDealAvg = activeDeals ? ndas30 / activeDeals : 0;
        const loiPerDealAvg = 0;

        // Lending — real data from firebase deal_documents
        let lenderOffers30 = 0;
        let avgOfferAmount: number | null = null;

        try {
          const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
          const offerSnap = await getDocs(query(
  collection(db, "dealDocuments"),
  where("documentType", "==", "offer")
));
const offerDocs = offerSnap.docs.map(d => d.data());
if (offerDocs) {
            lenderOffers30 = offerDocs.length;

            // Extract amounts from document_data if available
            const amounts = offerDocs
              .map((d: any) => {
                const data = d.document_data as Record<string, any> | null;
                return data?.amount || data?.loan_amount || data?.offered_amount || null;
              })
              .filter((a: any): a is number => typeof a === "number" && a > 0);

            if (amounts.length > 0) {
              avgOfferAmount = Math.round(amounts.reduce((s: number, v: number) => s + v, 0) / amounts.length);
            }
          }
        } catch (e) {
          console.error("Error fetching lender offers from Firebase:", e);
        }

        // Helper to calculate percentage change
        const calcChange = (current: number, previous: number) => {
          if (previous === 0) return current > 0 ? "+100%" : "—";
          const change = ((current - previous) / previous) * 100;
          const sign = change > 0 ? "+" : "";
          return `${sign}${change.toFixed(1)}% from last month`;
        };

        setKpis({
          activeDeals,
          deals30,
          deals30Change: calcChange(deals30, deals30to60),
          pipelineValue,
          ndas7,
          ndas30,
          ndas30Change: calcChange(ndas30, ndas30to60),
          ndas90,
          buyerSubs30,
          buyerSubs30Change: calcChange(buyerSubs30, buyerSubs30to60),
          newBuyers1,
          newBuyers7,
          newBuyers30,
          newBuyers30Change: calcChange(newBuyers30, newBuyers30to60),
          engagedBuyers30,
          dealsWithNda,
          dealsWithLoi,
          ndaPerDealAvg,
          loiPerDealAvg,
          lenderOffers30,
          avgOfferAmount,
        });
      } catch (err: any) {
        console.error("KPI Dashboard error:", err);
        setError(err?.message || "Failed to load KPI data. Please check Firebase permissions.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <SidebarShell>
      <header className="mb-8">
        <h1 className="text-4xl font-bold font-['Geist',sans-serif] mb-2">Dr. Carl KPIs.</h1>
        <h2 className="text-xl font-semibold opacity-80">Sales / Acquisition / Lending — KPIs</h2>
      </header>

      {loading && <div className="text-muted">Loading…</div>}
      {error && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6">
          <h3 className="text-lg font-semibold text-red-200 mb-2">Error Loading Data</h3>
          <p className="text-red-100/80">{error}</p>
          <p className="text-sm text-red-100/60 mt-2">
            This usually means Firebase security rules are not configured to allow read access to the required collections.
          </p>
        </div>
      )}

      {!loading && !kpis && (
        <div className="card-dark p-5">Unable to load KPIs. Check Firestore rules & indexes.</div>
      )}

      {kpis && (
        <div className="space-y-6">
          {/* Row 1: Core */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiTile title="Active Deals" value={kpis.activeDeals} icon={Briefcase} iconColor="text-blue-400" />
            <KpiTile title="Deals (30d)" value={kpis.deals30} subtitle={kpis.deals30Change} icon={TrendingUp} iconColor="text-green-400" />
            <KpiTile title="Pipeline Value" value={`$${kpis.pipelineValue.toLocaleString()}`} icon={DollarSign} iconColor="text-yellow-400" />
            <KpiTile title="NDAs 7d" value={kpis.ndas7} icon={FileText} iconColor="text-orange-400" />
          </div>

          {/* Row 2: NDA windows */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiTile title="NDAs 30d" value={kpis.ndas30} subtitle={kpis.ndas30Change} icon={FileText} iconColor="text-purple-400" />
            <KpiTile title="NDAs 90d" value={kpis.ndas90} icon={FileText} iconColor="text-pink-400" />
            <KpiTile title="Buyer Submissions (30d)" value={kpis.buyerSubs30} subtitle={kpis.buyerSubs30Change} icon={FileInput} iconColor="text-cyan-400" />
          </div>

          {/* Acquisition */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Acquisition (Buyers)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <KpiTile title="New Buyers 1d" value={kpis.newBuyers1} icon={UserPlus} iconColor="text-green-400" />
              <KpiTile title="New Buyers 7d" value={kpis.newBuyers7} icon={UserPlus} iconColor="text-emerald-400" />
              <KpiTile title="New Buyers 30d" value={kpis.newBuyers30} subtitle={kpis.newBuyers30Change} icon={UserPlus} iconColor="text-teal-400" />
              <KpiTile title="Engaged Buyers (30d)" value={kpis.engagedBuyers30} icon={Users} iconColor="text-blue-400" />
            </div>
          </section>

          {/* Matching & Pipeline */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Matching & Pipeline</h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <KpiTile title="Deals with NDA (proxy)" value={kpis.dealsWithNda} icon={FileCheck} iconColor="text-orange-400" />
              <KpiTile title="Deals with LOI (proxy)" value={kpis.dealsWithLoi} icon={FileCheck} iconColor="text-red-400" />
              <KpiTile title="NDA per Deal (avg)" value={kpis.ndaPerDealAvg.toFixed(2)} icon={Calculator} iconColor="text-yellow-400" />
              <KpiTile title="LOI per Deal (avg)" value={kpis.loiPerDealAvg.toFixed(2)} icon={Calculator} iconColor="text-amber-400" />
            </div>
          </section>

          {/* Lending */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Lending</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <KpiTile title="Lender Offers (30d)" value={kpis.lenderOffers30} icon={Wallet} iconColor="text-green-400" />
              <KpiTile title="Avg Offered Amount" value={kpis.avgOfferAmount ? `$${kpis.avgOfferAmount.toLocaleString()}` : "—"} icon={DollarSign} iconColor="text-yellow-400" />
              <KpiTile title="Time to First Offer (median)" value="—" subtitle="Add aggregate later" icon={Clock} iconColor="text-purple-400" />
            </div>
          </section>

          {/* Recent activity */}
          <div className="card-dark p-5">
            <div className="text-sm text-muted mb-1">Recent Activity</div>
            <div className="text-muted">Connect a "timeline" collection or query recent submissions/messages.</div>
          </div>
        </div>
      )}
    </SidebarShell>
  );
}
